import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, X, Send, RotateCcw, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../i18n/LanguageContext";
import { postSSE } from "../lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROVIDERS = [
  { key: "openai", label: "ChatGPT" },
  { key: "anthropic", label: "Claude" },
  { key: "gemini", label: "Gemini" },
];

export function AIChatWidget() {
  const { user } = useAuth();
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [provider, setProvider] = useState(localStorage.getItem("ai-provider") || "openai");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const sessionKey = `ai-session-${provider}`;

  useEffect(() => {
    if (!open) return;
    const sid = localStorage.getItem(sessionKey);
    if (!sid) { setMessages([]); return; }
    const headers = {};
    const school = localStorage.getItem("activeSchool");
    if (school) headers["X-School-Id"] = school;
    fetch(`${API}/ai/sessions/${sid}`, { credentials: "include", headers })
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => {
        if (s) setMessages(s.messages || []);
        else { localStorage.removeItem(sessionKey); setMessages([]); }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, provider]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  if (!user) return null;

  const switchProvider = (key) => {
    setProvider(key);
    localStorage.setItem("ai-provider", key);
  };

  const newChat = () => {
    localStorage.removeItem(sessionKey);
    setMessages([]);
  };

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setBusy(true);
    try {
      await postSSE("/ai/chat", {
        message: text,
        provider,
        session_id: localStorage.getItem(sessionKey) || "",
      }, {
        onDelta: (d) => setMessages((m) => {
          if (!m.length || m[m.length - 1].role !== "assistant") {
            return [...m, { role: "assistant", content: d || "" }];
          }
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", content: (copy[copy.length - 1].content || "") + (d || "") };
          return copy;
        }),
        onDone: (ev) => { if (ev.session_id) localStorage.setItem(sessionKey, ev.session_id); },
        onError: (msg) => toast.error(msg),
      });
    } catch (e) {
      toast.error(e.message || "Error de IA");
      setMessages((m) => m.filter((x) => x.content !== ""));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50" data-testid="ai-chat-widget">
      {open && (
        <div
          className="mb-3 w-[380px] max-w-[calc(100vw-2.5rem)] h-[540px] max-h-[70vh] bg-white border border-slate-200 rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          data-testid="ai-chat-panel"
        >
          <div className="bg-slate-900 text-white px-4 py-3 flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-sky-300" />
            <span className="text-sm font-semibold flex-1">{t("ai.title")}</span>
            <button data-testid="ai-chat-new" title={t("ai.newChat")} onClick={newChat}
              className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
              <RotateCcw className="w-4 h-4" />
            </button>
            <button data-testid="ai-chat-close" title="Cerrar" onClick={() => setOpen(false)}
              className="p-1.5 rounded-lg hover:bg-slate-700 transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="flex gap-1.5 px-3 py-2 border-b border-slate-200 bg-slate-50">
            {PROVIDERS.map((p) => (
              <button
                key={p.key}
                data-testid={`ai-provider-${p.key}`}
                onClick={() => switchProvider(p.key)}
                className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
                  provider === p.key
                    ? "bg-blue-800 text-white border-blue-800"
                    : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-3 py-3 space-y-3" data-testid="ai-chat-messages">
            {messages.length === 0 && (
              <p className="text-xs text-slate-400 text-center mt-8 px-4" data-testid="ai-chat-empty">
                {user.role === "padre"
                  ? "Hola, soy el asistente del colegio. Pregúntame sobre pagos, tareas o información general."
                  : user.role === "maestro"
                    ? "Hola, puedo ayudarte a redactar comunicados, planes de clase, actividades y comentarios de evaluación."
                    : "Hola, puedo ayudarte con la gestión del colegio, redacción de documentos y más."}
              </p>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-blue-800 text-white rounded-br-md"
                      : "bg-slate-100 text-slate-800 rounded-bl-md"
                  }`}
                >
                  {m.content || (busy && i === messages.length - 1 ? "…" : "")}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-slate-200 p-3 flex gap-2">
            <input
              data-testid="ai-chat-input"
              className="flex-1 text-sm border border-slate-200 rounded-xl px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-800/30"
              placeholder={t("ai.placeholder")}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
              disabled={busy}
            />
            <button
              data-testid="ai-chat-send"
              onClick={send}
              disabled={busy || !input.trim()}
              className="bg-blue-800 hover:bg-blue-700 disabled:opacity-50 text-white rounded-xl px-3.5 transition-colors"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}
      <button
        data-testid="ai-chat-button"
        onClick={() => setOpen(!open)}
        className="ml-auto flex items-center gap-2 bg-blue-800 hover:bg-blue-700 text-white rounded-full pl-4 pr-5 py-3 shadow-xl transition-transform hover:scale-105"
      >
        <Sparkles className="w-5 h-5" />
        <span className="text-sm font-semibold">IA</span>
      </button>
    </div>
  );
}
